import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { NavData, navItems } from '../../_nav';
import { AuthService } from '../../shared/auth.service';
import { User } from '../../shared/interfaces';
import { LoaderService } from '../../shared/loader.service';
import { UserService } from '../../user/user.service';
import { AppSidebarService } from '../custom/sidebar/app-sidebar.service';

@Component({
  selector: 'app-dashboard',
  styleUrls: ['./default-layout.component.scss'],
  templateUrl: './default-layout.component.html',
})
export class DefaultLayoutComponent implements OnDestroy, OnInit {
  public navItems = [];
  public sidebarMinimized = true;
  private changes: MutationObserver;
  public element: HTMLElement;
  public user: User;
  public loading = false;
  public sidebarOpen = true;
  private sidebarStatusSubscription: Subscription;
  public hasCustomAPI = !!localStorage.getItem('customAPI');
  public production: boolean = true;

  constructor(
    public authService: AuthService,
    public userService: UserService,
    public loaderService: LoaderService,
    private sidebarService: AppSidebarService,
    @Inject(DOCUMENT) _document?: any,
  ) {
    this.production = window.location.hostname.includes('dashboard.losscontrol.plus');
    this.changes = new MutationObserver(mutations => {
      this.sidebarMinimized = _document.body.classList.contains('sidebar-minimized');
    });
    this.element = _document.body;
    this.changes.observe(<Element>this.element, {
      attributes: true,
      attributeFilter: ['class'],
    });
  }

  ngOnDestroy(): void {
    this.changes.disconnect();
    this.sidebarStatusSubscription.unsubscribe();
  }

  get isImpersonated() {
    return !!this.authService.impersonatedToken;
  }

  leaveImpersonation() {
    this.authService.leaveImpersonation();
  }

  async ngOnInit() {
    this.sidebarOpen = this.sidebarService.isSidebarOpen();
    setTimeout(() => {
      this.sidebarService.setSidebarOpen(this.sidebarOpen);
    }, 1);
    this.sidebarStatusSubscription = this.sidebarService.sidebarStatus$.subscribe(status => {
      this.sidebarOpen = status;
    });
    this.loaderService.active.subscribe(l => {
      setTimeout(() => (this.loading = l), 1);
    });
    this.user = await this.userService.profile();
    this.navItems = await this.filterNav(navItems);

    const togglers = document.querySelectorAll('.navbar-toggler-icon');

    togglers.forEach(t => {
      t.addEventListener('click', e => {
        setTimeout(() => {
          const sidebarShown = !!document.querySelector('.sidebar-md-show');

          if (sidebarShown) {
            document.querySelector('.navbar-brand-full').setAttribute('src', 'assets/img/brand/full-white.png');
          } else {
            document.querySelector('.navbar-brand-full').setAttribute('src', 'assets/img/brand/sygnet.png');
          }
        }, 1);
      });
    });

    setInterval(_ => (this.hasCustomAPI = !!localStorage.getItem('customAPI')), 1000);
  }

  resetAPI() {
    localStorage.removeItem('customAPI');
  }

  async filterNav(navList: NavData[]) {
    if (!navList) {
      return;
    }

    const filtered = [];

    for (const nav of navList) {
      if (nav.features && !(await this.userService.hasFeatures(nav.features))) {
        continue;
      }

      if (nav.userTypes && !nav.userTypes.includes(this.user.type)) {
        continue;
      }

      if (nav.permissions && !(await this.userService.hasPermissions(nav.permissions))) {
        continue;
      }
      if ('children' in nav) {
        filtered.push({
          ...nav,
          children: await this.filterNav(nav.children),
        });
      } else {
        filtered.push(nav);
      }
    }

    return filtered.filter(nav => {
      // Removing empty higher level menus
      return !('children' in nav) || nav.children.length;
    });
  }

  toggleSidebar() {
    const changedValue = !this.sidebarOpen;
    this.sidebarOpen = changedValue;
    this.sidebarService.setSidebarOpen(changedValue);
  }

  logout() {
    this.authService.logout();
  }
}
